// Global Instructions Rule Applied!
//
// The words the distribution form uses for a per-commitment line and for the
// state of a tier's bucket - shared by the Allocation grid, the Tiers strip,
// the expanded row and the Taken first panel.

import type {
  DistributionPerCommitment,
  DistributionTierState
} from '../../../../state_manager/data_models/distribution_detail.ts'

export const CALCULATED_TITLE = 'Calculated'
export const CORRECTION_TITLE = 'Correction'
export const OVERRIDE_TITLE = 'Override'
export const EFFECTIVE_TITLE = 'Effective'

export const CORRECTION_HELP =
  'What the check of earlier distributions adds to or takes off the tier-by-tier figure: the entitlement to date less what was received to date. Within an investor it moves money between its cash and its carry, so the two net to nil. Empty where earlier distributions followed the rules. How they are checked - to date, as distributed, or not at all - is set on the fund Terms tab (Waterfall, Earlier distributions).'

const PRIORITY_LABELS: Record<string, string> = {
  special_distribution: 'Taken first: special distribution of waived fees',
  sba: 'Taken first: SBA',
  lender_repayment: 'Taken first: lender repayment'
}

/** What the row pays under: the tier, the priority line or the GP's own share. */
export function rowTierLabel(row: DistributionPerCommitment): string {
  if (row.kind === 'priority') return PRIORITY_LABELS[row.priority_kind ?? ''] ?? 'Taken first'
  if (row.kind === 'own_share') return 'Own share (the GP commitment)'
  const seq = row.tier_sequence != null ? `Tier ${row.tier_sequence}` : 'Tier'
  return row.tier_type_label ? `${seq} · ${row.tier_type_label}` : seq
}

/** `Tier 2 · Profit Split` */
export function tierTitle(sequence: number, typeLabel: string | null | undefined): string {
  return typeLabel ? `Tier ${sequence} · ${typeLabel}` : `Tier ${sequence}`
}

/** The metric the engine reports for the Cross-Pool Loss Offset tier's test (a waterfall with pools). */
export const METRIC_LOSS_OFFSET = 'Loss Offset'

/** A figure worth printing: not absent, and not nil at the cent. */
export const hasFigure = (v: number | null | undefined): v is number => v != null && Math.abs(v) >= 0.005

export const fmtRate = (fraction: number | null | undefined): string =>
  fraction == null ? '—' : `${(fraction * 100).toFixed(2).replace(/\.?0+$/, '')}%`

/**
 * The bucket of a tier after this distribution, in the Tag vocabulary: green
 * Full (satisfied), blue while it is still filling, default where the run
 * never got to it. A tier without a stop has no bucket and says nothing;
 * null = the distribution was computed before the tests were kept.
 */
export function tierStateTag(
  state: DistributionTierState | null | undefined
): { color: string; label: string } | null {
  if (state === 'full') return { color: 'green', label: 'Full' }
  if (state === 'open') return { color: 'blue', label: 'to go' }
  if (state === 'not_reached') return { color: 'default', label: 'Not reached' }
  return null
}
